import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { Button, Tab, Tabs, Typography } from '@mui/material';
import SubscriptionPlansColumn from './subscriptionPlans.column';
import { useNavigate, useSearchParams } from 'react-router-dom';
import InitiativePlans from '../initiativePlans/initiativePlans';
import { getAllSubscription, getFinancialSupportProducts } from './subscriptionPlans.service';
import { getAllCountriesFlat } from '../../countryManagement/countryManagement.service';
import { Icon } from "@iconify/react";
import GenericDataGrid from '../../../components/genericTable';
import { createManagementTableSx } from '../../../styles/tableStyles';
import { managementCreateButtonSx, managementHeaderActionSx, managementHeaderContentSx, managementHeaderSx, managementPageSubtitleSx, managementPageTitleSx } from '../../../styles/managementStyles';
import AlertToast from '../../../components/alertToast';
import { buttonContentSx } from '../../../styles/dialogStyles';
import InlineAlert from '../../../components/inlineAlert';

export default function SubscriptionPlans() {

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'initiative' ? 'initiative' : 'signup';
  const [subcriptionData, setSubcriptionData] = useState([]);
  const [errors] = useState('');
  const [loading, setLoading] = useState(false);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [currenntPagemain, setCurrentPagemain] = useState(0);
  const [rowsperpagemain, setrowsperpagemain] = useState(10);
  const [alert, setAlert] = useState('');
  const [openAlert, setOpenAlert] = useState(false);
  const [responseMsg, setResponseMsg] = useState('');
  const [countryMap, setCountryMap] = useState({});
  const [currencySymbolMap, setCurrencySymbolMap] = useState({});
  const [financialSupportProductMap, setFinancialSupportProductMap] = useState({});

  const alertClose = () => {
    setAlert('');
    setOpenAlert(false);
  };

  const sortByCreatedAtDesc = (rows = []) => {
    return [...rows].sort((a, b) => {
      const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
  };

  const getSubscriptionData = useCallback(async (page = currenntPagemain, size = rowsperpagemain) => {
    setLoading(true);
    await getAllSubscription(page, size).then(response => {
      setLoading(false);
      const rows = Array.isArray(response?.content) ? response.content : [];
      setSubcriptionData(sortByCreatedAtDesc(rows));
      setTotalDataCount(response.totalElements);
    }).catch(error => {
      setLoading(false);
      setSubcriptionData([]);
      setTotalDataCount(0);
    });
  }, [currenntPagemain, rowsperpagemain]);

  useEffect(() => {
    getSubscriptionData(currenntPagemain, rowsperpagemain)
  }, [currenntPagemain, rowsperpagemain, getSubscriptionData]);

  useEffect(() => {
    const loadCountries = async () => {
      const countries = await getAllCountriesFlat();
      const map = {};
      const currencyMap = {};
      countries.forEach((country) => {
        if (country?.id) {
          map[country.id] = (country?.name || country?.countryName || '').toString().trim();
        }
        const code = (country?.currencyCode || country?.currency || '').toString().trim();
        const symbol = (country?.currencySymbol || '').toString().trim();
        if (code && symbol && !currencyMap[code]) {
          currencyMap[code] = symbol;
        }
      });
      setCountryMap(map);
      setCurrencySymbolMap(currencyMap);
    };
    loadCountries();
  }, []);

  useEffect(() => {
    const loadFinancialSupportProducts = async () => {
      const products = await getFinancialSupportProducts();
      const map = {};
      (Array.isArray(products) ? products : []).forEach((product) => {
        if (product?.id) {
          map[product.id] = (product?.name || '').toString().trim();
        }
      });
      setFinancialSupportProductMap(map);
    };
    loadFinancialSupportProducts();
  }, []);

  let subscription = {
    'size': '',
    'page': '',
    'navigate': navigate,
    'getSubscriptionData': getSubscriptionData,
    'setAlert': setAlert,
    'setOpenAlert': setOpenAlert,
    'setResponseMsg': setResponseMsg,
    'setLoading': setLoading,
    'countryMap': countryMap,
    'currencySymbolMap': currencySymbolMap,
    'financialSupportProductMap': financialSupportProductMap
  }

  const subscriptionData = SubscriptionPlansColumn(subscription);
  const subscriptionGridSx = createManagementTableSx();

  return (
    <>
      <Box sx={managementHeaderSx}>
        <Box sx={managementHeaderContentSx}>
          <Typography className="app-page-title" sx={managementPageTitleSx}>
            Subscription Plans
          </Typography>
          <Typography sx={managementPageSubtitleSx}>
            Manage subscription plans available for platform licenses.
          </Typography>
        </Box>
        <Box sx={managementHeaderActionSx}>
          <Button
            variant="contained"
            type="submit"
            onClick={() => { setLoading(true); navigate(tab === 'initiative' ? '/addInitiativePlan' : '/addSubcriptions'); }}
            sx={{ ...managementCreateButtonSx, width: { xs: '100%', md: 'auto' }, minWidth: { xs: '100%', md: 244 } }}
          >
            <Box component="span" sx={buttonContentSx}>
              <Icon icon="tabler:plus" width="18" height="18" />
              <span>{tab === 'initiative' ? 'CREATE INITIATIVE PLAN' : 'CREATE SUBSCRIPTION'}</span>
            </Box>
          </Button>
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, value) => setSearchParams(value === 'initiative' ? { tab: 'initiative' } : {})}
        aria-label="Subscription plan types"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="signup" label="Sign-up plans" id="plans-tab-signup" aria-controls="plans-panel-signup" />
        <Tab value="initiative" label="Initiative plans" id="plans-tab-initiative" aria-controls="plans-panel-initiative" />
      </Tabs>

      {tab === 'initiative' && (
        <Box role="tabpanel" id="plans-panel-initiative" aria-labelledby="plans-tab-initiative">
          <InitiativePlans />
        </Box>
      )}

      <Box role="tabpanel" id="plans-panel-signup" aria-labelledby="plans-tab-signup" hidden={tab !== 'signup'}>
      <Typography sx={{ ...managementPageSubtitleSx, mb: 3, maxWidth: 760 }}>
        The plans a person sees when they sign up on eK Link. Set the price, the billing period and the features for each
        user type and country.
      </Typography>

      <InlineAlert
        message={openAlert && alert === 'false' ? responseMsg : ''}
        onClose={alertClose}
        variant="error"
      />

      <Box sx={{ width: '100%' }}>
        <GenericDataGrid rows={subcriptionData} columns={subscriptionData} rowCount={totalDataCount} page={currenntPagemain} pageSize={rowsperpagemain} loading={loading} errors={errors} checkbox='false'
          dataGridSx={subscriptionGridSx}
          rowHeight={60}
          onPaginationChange={(page, pageSize) => {
            setCurrentPagemain(page);
            setrowsperpagemain(pageSize);
            getSubscriptionData(page, pageSize);
          }}
        />
      </Box>

      <Box textAlign={'end'}>
        <AlertToast open={openAlert && alert === 'true'} onClose={alertClose} message={responseMsg} variant="success" />
      </Box>
      </Box>
    </>
  );
}
